import { useEffect, useState } from 'react';
import { apiClient } from '@/api/client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/auth/AuthContext';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function Settings() {
    const { user } = useAuth();
    const [status, setStatus] = useState(null);

    useEffect(() => {
        apiClient.get('/integrations/meta/status').then((r) => setStatus(r.data));
    }, []);

    return (
        <div className="space-y-4">
            <div>
                <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
                <p className="text-sm text-muted-foreground">Operator identity, integrations, and connection diagnostics.</p>
            </div>
            <Card className="border-border bg-card p-4">
                <h2 className="text-base font-semibold">Operator</h2>
                <ul className="mt-2 text-sm">
                    <li><span className="text-muted-foreground">Email:</span> <span className="font-mono">{user?.email}</span></li>
                    <li><span className="text-muted-foreground">Role:</span> <span className="font-mono">{user?.role}</span></li>
                    <li><span className="text-muted-foreground">Created:</span> <span className="font-mono">{user?.created_at && new Date(user.created_at).toLocaleString()}</span></li>
                </ul>
            </Card>
            <Card className="border-border bg-card p-4">
                <h2 className="text-base font-semibold">Meta (Instagram) integration</h2>
                {!status ? (
                    <div className="text-sm text-muted-foreground">Loading…</div>
                ) : (
                    <div className="mt-2 space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                            {status.placeholder_mode ? (
                                <AlertCircle className="h-4 w-4 text-[hsl(var(--warning))]" />
                            ) : (
                                <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))]" />
                            )}
                            <span>{status.placeholder_mode ? 'Placeholder mode — real publishing disabled' : 'Connected'}</span>
                        </div>
                        <ul className="font-mono text-[12px] text-muted-foreground">
                            <li>app_id_present: {String(status.app_id_present)}</li>
                            <li>app_secret_present: {String(status.app_secret_present)}</li>
                            <li>verify_token_present: {String(status.verify_token_present)}</li>
                            <li>redirect_uri: {status.redirect_uri}</li>
                        </ul>
                        <p className="rounded-md border border-dashed border-border bg-[hsl(var(--surface-2))] p-3 text-[12px] text-muted-foreground">
                            {status.note}
                        </p>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={status.placeholder_mode}
                            onClick={() => toast.info('OAuth flow would open here once real credentials are configured.')}
                        >
                            Begin Meta OAuth
                        </Button>
                    </div>
                )}
            </Card>
            <Card className="border-border bg-card p-4">
                <h2 className="text-base font-semibold">Setup instructions</h2>
                <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
                    <li>Create a Meta Business / Developer App with Instagram Graph API.</li>
                    <li>Add Instagram Professional accounts and link them to a Facebook Page.</li>
                    <li>Subscribe your app to the relevant webhooks (comments, messaging).</li>
                    <li>Set <code className="font-mono">META_APP_ID</code>, <code className="font-mono">META_APP_SECRET</code>, <code className="font-mono">META_REDIRECT_URI</code> in <code className="font-mono">backend/.env</code>.</li>
                    <li>Restart the backend service. The integration banner above should switch to green.</li>
                    <li>From <strong>Accounts</strong>, run the Meta OAuth flow per brand to attach real tokens.</li>
                </ol>
            </Card>
        </div>
    );
}
